/*
Upload Pipeline:
1. Receive image/video
1. Calculate image (video?) hash
1. Create Upload request
  1. Cancel if cancelled (duplicate hash)
1. Upload to endpoint
1. Confirm delivery of upload and receive additional data
*/
import { create } from 'zustand'
import { produce } from 'immer'
import { Album } from '@/models/album.ts'
import { AlbumFile } from '@/models/albumFile.ts'
import HeyGame from '@/models/heyGame.ts'
import AlbumGuestbookEntry from '@/models/albumGuestbookEntry.ts'

export enum UploadFileStatus {
  Added = 'added',
  InProgress = 'in_progress',
  HashCompleted = 'hash_completed',
  Uploading = 'uploading',
  Success = 'success',
  Failed = 'failed',
}

export function isStatusFinalState(status: UploadFileStatus) {
  return status === UploadFileStatus.Success || status === UploadFileStatus.Failed
}

interface UploadPostParts {

}

export interface BytesHistoryPoint {
  bytes: number
  time: number
}

export function calculateKibiBytesPerSecond(bytesHistory: Array<BytesHistoryPoint>): number | null {
  if (bytesHistory.length < 3) {
    return null
  }

  let kibiBytesPerSecond = 0
  for (let i = 0; i < bytesHistory.length - 1; i++) {
    const fromPoint = bytesHistory[i + 1]
    const toPoint = bytesHistory[i]
    const timeElapsed = toPoint.time - fromPoint.time
    if (timeElapsed == 0) {
      continue
    }
    kibiBytesPerSecond += (toPoint.bytes - fromPoint.bytes) / timeElapsed / bytesHistory.length
  }

  return kibiBytesPerSecond
}

export interface UploadFile {
  randomId: string
  file: File
  game?: HeyGame
  guestbookEntry?: AlbumGuestbookEntry
  fileHash?: string | null
  uploadPost?: string | Array<UploadPostParts> | null
  status: UploadFileStatus
  album: Album
  bytesUploaded: number | null
  bytesHistory?: Array<BytesHistoryPoint>
}

interface UseUploadsState {
  uploadQueue: Array<UploadFile>
  uploadedFiles: Record<Album['id'], Array<AlbumFile>>
  deletedFiles: Set<string>
}

interface RegisterUploadFile {
  file: File
  game?: HeyGame
  guestbookEntry?: AlbumGuestbookEntry
}

interface UseUploadsActions {
  registerFileUploads: (album: Album, files: Array<RegisterUploadFile>) => Array<UploadFile>
  updateFileUpload: (uploadFile: UploadFile) => void
  addUploadedFiles: (album: Album, files: Array<AlbumFile>) => void
  addDeletedFiles: (files: Array<AlbumFile>) => void
  removeUploadedFilesForAlbum: (album: Album) => void
  updateUploadedFile: (updatedFile: AlbumFile) => void
  clearQueue: () => void
  // setDrive: (drive: HeyDrive) => void
}

function initUploadFile(album: Album, registerFile: RegisterUploadFile): UploadFile {
  let randomId: string | undefined
  try {
    randomId = crypto.randomUUID()
  } catch (e) {
    console.log('Err generating random id', e)
    randomId = `${Math.random()}:${Math.random()}:${Math.random()}`
  }

  return {
    randomId,
    file: registerFile.file,
    game: registerFile.game,
    guestbookEntry: registerFile.guestbookEntry,
    fileHash: undefined,
    uploadPost: undefined,
    status: UploadFileStatus.Added,
    bytesUploaded: null,
    album,
  }
}

export const useUploadQueue = create<UseUploadsState & UseUploadsActions>((set) => ({
  uploadQueue: [],
  uploadedFiles: {},
  deletedFiles: new Set(),
  registerFileUploads(album: Album, files) {
    const uploadQueueFiles = files.map(file => initUploadFile(album, file))
    set(produce((state: UseUploadsState) => {
      state.uploadQueue.push(...uploadQueueFiles)
    }))

    return uploadQueueFiles
  },
  updateFileUpload(file) {
    set(produce((state: UseUploadsState) => {
      const i = state.uploadQueue.findIndex(uploadFile => uploadFile.randomId === file.randomId)
      if (i >= 0) {
        state.uploadQueue[i] = file
      }
    }))
  },
  addUploadedFiles(album: Album, files: Array<AlbumFile>) {
    set(produce((state: UseUploadsState) => {
      const currentlyUploaded = state.uploadedFiles[album.id] || []

      state.uploadedFiles[album.id] = [...files, ...currentlyUploaded, ]
    }))
  },
  addDeletedFiles(files: Array<AlbumFile>) {
    set(produce((state: UseUploadsState) => {
      const newFiles = new Set(state.deletedFiles)

      for (const file of files) {
        newFiles.add(file.id)
      }

      state.deletedFiles = newFiles
    }))
  },
  removeUploadedFilesForAlbum(album: Album) {
    set(produce((state: UseUploadsState) => {
      state.uploadedFiles = {
        ...state.uploadedFiles,
        [album.id]: [],
      }
    }))
  },
  updateUploadedFile(updatedFile: AlbumFile) {
    set(produce((state: UseUploadsState) => {
      const newUploadedFiles: Record<string, Array<AlbumFile>> = {}
      Object.entries(state.uploadedFiles).map(([albumId, uploadedFiles]) => newUploadedFiles[albumId] = uploadedFiles.map(file => file.id === updatedFile.id ? updatedFile : file))
      state.uploadedFiles = newUploadedFiles
    }))
  },
  clearQueue() {
    set(produce((state: UseUploadsState) => {
      state.uploadQueue = []
    }))
  },
}))
